/**
 * The blueprint and the hull.
 *
 * A player owns a *design* — the thing they drew in the editor, with every line
 * whole. What they fly is a *hull*: the same graph, carrying whatever has
 * happened to it since. Keeping the two apart is what makes damage recoverable
 * rather than destructive: the editor never has to know about dents, and losing
 * a ship costs you a repair bill instead of your work.
 *
 * It is also what a rebuy *is*. Insurance does not reconstruct a wreck; it
 * builds you a fresh hull from a blueprint you still own. Once that blueprint
 * lives on the server this file barely changes — `freshHull` is already exactly
 * the operation, and the only thing missing is somebody charging for it.
 *
 * ── Strength reduces damage, health absorbs it ──────────────────────────────
 *
 * Every line starts with the same hundred points. What an upgrade buys is
 * strength, which divides incoming damage — so a sintered line takes a
 * fifteenth of what a standard one does from the same hit. That is the whole
 * progression: one axis, legible, and the same rule wherever damage comes from.
 */
import { findCompartments } from '../geometry/compartments.js';
import { LINE_HEALTH } from './upgrades.js';
/**
 * A new hull built to a design, every line whole.
 *
 * Deep enough to be independent: the flight loop takes damage on the hull, and
 * a shallow copy would write it straight back into the blueprint.
 */
export function freshHull(blueprint) {
    return {
        joints: blueprint.joints.map((joint) => ({ ...joint })),
        lines: blueprint.lines.map((line) => ({ ...line, health: LINE_HEALTH })),
        components: blueprint.components.map((component) => ({ ...component })),
    };
}
/**
 * The lines on the outside of the ship.
 *
 * A line is hull when fewer than two compartments claim it: the outside is on
 * the other side of it. Struts enclosing nothing count too, because they are
 * just as exposed. This is the same rule the renderer uses to decide what to
 * draw as skin, and it lives here so that what looks like the outside of the
 * ship and what *is* the outside of the ship cannot drift apart.
 */
export function perimeterLineIds(graph) {
    const claims = new Map();
    for (const compartment of findCompartments(graph)) {
        for (const id of compartment.lineIds)
            claims.set(id, (claims.get(id) ?? 0) + 1);
    }
    return new Set(graph.lines.filter((line) => (claims.get(line.id) ?? 0) < 2).map((l) => l.id));
}
/**
 * The perimeter lines a contact at this point lands on, in the ship's frame.
 *
 * Damage used to be spread across every line in the ship on the reasoning that a
 * collision is the hull meeting a surface rather than a shot placed on a wall.
 * That is true, and it still put dents in the far side of the ship and in
 * internal partitions that nothing touched — so a hull could be worn out from
 * the inside by scraping one corner.
 *
 * Interior walls are never exposed, so they are never eligible. Among the
 * perimeter, the lines within `spread` of the contact take it; if the contact is
 * further than that from anything, the single nearest line does, so a hit is
 * never simply lost.
 */
export function contactLines(graph, at, spread) {
    /*
     * Exposure, from the ship as it currently is.
     *
     * Nothing to reconcile: a destroyed wall is not in the graph, so a partition
     * behind one is simply on the outside now and traces that way. This used to
     * need a filter and a comment explaining which version of the ship it meant.
     */
    const perimeter = perimeterLineIds(graph);
    const joints = new Map(graph.joints.map((joint) => [joint.id, joint]));
    let nearest = null;
    let nearestGap = Infinity;
    const struck = new Set();
    for (const line of graph.lines) {
        if (!perimeter.has(line.id))
            continue;
        const a = joints.get(line.a);
        const b = joints.get(line.b);
        if (!a || !b)
            continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const lengthSq = dx * dx + dy * dy;
        // Distance to the segment, not to its ends — a long wall struck in the
        // middle is struck in the middle.
        const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((at.x - a.x) * dx + (at.y - a.y) * dy) / lengthSq));
        const gap = Math.hypot(at.x - (a.x + dx * t), at.y - (a.y + dy * t));
        if (gap <= spread)
            struck.add(line.id);
        if (gap < nearestGap) {
            nearestGap = gap;
            nearest = line.id;
        }
    }
    if (struck.size === 0 && nearest)
        struck.add(nearest);
    return struck;
}
/**
 * Take damage off a hull.
 *
 * `lines` names which ones are in the way. Without it every line takes the hit,
 * which is still what a hull-wide event wants — but a collision should pass
 * `contactLines`, or the far side of the ship dents when you scrape a corner.
 *
 * Strength divides rather than subtracts, so an upgrade keeps mattering at every
 * scale of hit instead of only against small ones.
 */
export function damageHull(hull, damage, lines) {
    if (damage <= 0)
        return hull;
    return {
        ...hull,
        lines: hull.lines.map((line) => lines && !lines.has(line.id)
            ? line
            : { ...line, health: Math.max(0, line.health - damage / Math.max(1, line.strength)) }),
    };
}
/**
 * How much of a repair to buy.
 *
 * A full repair is one price, and a ship limping into a station with less than
 * that is stuck: it can afford *something* and the only button costs everything.
 * These are the fractions the yard will sell, and they buy that fraction of the
 * damage back.
 *
 * Proportional throughout -- a tenth of the missing health for a tenth of the
 * bill -- so partial repairs are never cheaper or dearer per point than the
 * whole. Buying in tenths and buying once come to the same money.
 */
export const REPAIR_STEPS = [0.1, 0.25, 0.5, 1];
/** A fraction, kept inside 0..1 so a bad request cannot conjure health. */
function share(fraction) {
    if (fraction === undefined)
        return 1;
    if (!Number.isFinite(fraction))
        return 0;
    return Math.min(1, Math.max(0, fraction));
}
/** Put health back into one line, leaving the rest of the hull as it is. */
export function repairLine(hull, lineId, fraction = 1) {
    const part = share(fraction);
    return {
        ...hull,
        lines: hull.lines.map((line) => line.id === lineId ? { ...line, health: restored(line, part) } : line),
    };
}
/** Put health back into every line at once. */
export function repairHull(hull, fraction = 1) {
    const part = share(fraction);
    return {
        ...hull,
        lines: hull.lines.map((line) => ({ ...line, health: restored(line, part) })),
    };
}
/**
 * A line's health after buying `part` of what it is missing.
 *
 * Of the *missing* health, not of the maximum -- so a fully repaired wall is
 * exactly whole, and a tenth-repair on a wall at 40 buys six points rather than
 * ten. Clamped at both ends: a negative health (which nothing should produce,
 * but which a stored record could carry) must not repair to less than it was.
 */
function restored(line, part) {
    const health = Math.max(0, line.health);
    return Math.min(LINE_HEALTH, health + (LINE_HEALTH - health) * part);
}
/** What putting one line back to whole would cost, in hull points. */
export function lineRepairWork(hull, lineId) {
    const line = hull.lines.find((l) => l.id === lineId);
    if (!line)
        return 0;
    return Math.max(0, LINE_HEALTH - Math.max(0, line.health));
}
/**
 * How much ship is left, 0..1.
 *
 * Measured against `whole` where one is given, and that is not a detail: damage
 * *removes* walls now, so a hull measured against itself would report a higher
 * number the more of it fell off — a ship down to one pristine wall would read a
 * hundred per cent. Against the plan it falls the way it should.
 *
 * Without a plan it falls back to the hull's own lines, which is right for the
 * case that has no plan to be measured against: a loaner handed out after a loss
 * has nothing to do with the design its owner drew.
 */
export function hullIntegrity(hull, whole) {
    const total = (whole ?? hull).lines.length * LINE_HEALTH;
    if (total <= 0)
        return 1;
    const left = hull.lines.reduce((sum, line) => sum + Math.max(0, line.health), 0);
    return Math.max(0, Math.min(1, left / total));
}
/** Lines that have given out. What the compartment tracer will want. */
export function brokenLines(hull) {
    return hull.lines.filter((line) => line.health <= 0).map((line) => line.id);
}
/**
 * Whether there is anything left to fly.
 *
 * Nothing left at all. Walls are removed as they are destroyed, so this is now
 * simply the end of that process rather than a rule about which walls count —
 * and the more common way to lose a ship is `canStillFly` failing first, when
 * the compartment with the seat or the engine in it goes.
 */
export function isHullLost(hull) {
    return hull.lines.length === 0 || hull.lines.every((line) => line.health <= 0);
}
/**
 * What restoring this hull would cost, in hull points.
 *
 * Proportional to what is missing, so a scrape is cheap and a wreck is not. The
 * rate is not set here — that is an economy question, and this returns the
 * *work* for whoever is charging to price.
 */
export function repairWork(hull) {
    return hull.lines.reduce((work, line) => work + Math.max(0, LINE_HEALTH - Math.max(0, line.health)), 0);
}
/**
 * Linelits per unit of line, to put a whole one back.
 *
 * The cost of a repair is what is missing from the wall, as a fraction, times
 * how much wall there is — so a scratch on a long span and a scratch on a short
 * one differ by exactly the amount of wall involved, which is the only thing
 * that should make them differ.
 *
 * This has been wrong in both directions. It started as a flat rate per hull
 * point so small it made damage free — a wall destroyed end to end cost forty
 * pence — and the correction overshot into charging by the point regardless of
 * how much wall the point was on, which billed a short strut and a long span the
 * same and made repairs cost more than the ship. Length is what was missing.
 */
export const REPAIR_PER_UNIT = 3;
/** What putting one wall back costs, in linelits. `fraction` of the damage. */
export function lineRepairCost(line, fraction = 1) {
    const missing = Math.max(0, LINE_HEALTH - Math.max(0, line.health)) / LINE_HEALTH;
    return missing * line.length * REPAIR_PER_UNIT * share(fraction);
}
/** What putting the whole hull back costs, in linelits. */
export function repairCost(hull, fraction = 1) {
    return hull.lines.reduce((bill, line) => bill + lineRepairCost(line, fraction), 0);
}
//# sourceMappingURL=hull.js.map